/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect } from "react";
import TableTemplate from "./Table";
import CardLayout from "../Cards/CardLayout";
import { useUtils } from "../../customHooks/useUtils";
import { useFetchData } from "../../customHooks/useGetDashboardData";
import { REFETCH_CONFIGURATION, ROWS_PER_PAGE } from "../../utils/constants";
import Loader from "../feedBacks/loader";
import CardError from "../feedBacks/CardError";
import NoContent from "../feedBacks/NoContent";
import Loader2 from "../feedBacks/loader2";
// import HierarchicalDeviceSelector from "../Charts/HierarchicalDeviceSelector";
import SelectedDeviceTags from "./SelectedDeviceTag";
import HierarchicalDeviceSelector from "../Charts/SearchHableHieracrhical";
import AppButton from "../Inputs/AppButton";
import { ButtonType } from "../../enums/componentEnums";
import { formatCustomDate } from "../../utils/utils";

const AlertHistoryTable = ({ company_id }: { company_id?: string }) => {
  const { t } = useUtils();
  const [page, setPage] = React.useState(1);
  const [isNextPageLoading, setIsNextPageLoading] = React.useState(false);
  const filter = [
    { key: t("alert.critical"), value: "critical-alerts-daily" },
    { key: t("alert.warning"), value: "warning-alerts" },
  ];

  // Separate current applied values from temporary selection values
  const [selectedDevices, setSelectedDevices] = React.useState<string[]>([]);
  const [selectedFilter, setSelectedFilter] = React.useState("warning-alerts");

  // Temporary states for user selections (not applied until button click)
  const [tempSelectedDevices, setTempSelectedDevices] = React.useState<
    string[]
  >([]);
  const [tempSelectedFilter, setTempSelectedFilter] =
    React.useState("warning-alerts");

  const columns = [
    t("date_time"),
    t("alert.type"),
    t("alert.site"),
    t("alert.zone"),
    t("alert.threshold"),
    t("alert.detected_value"),
    t("alert.parameter"),
  ];

  const {
    data,
    isLoading: alertsLoading,
    error,
    refetch,
  }: any = useFetchData(
    [
      "/cloud-watch-alerts",
      "fetchAllAlerts",
      company_id,
      selectedDevices,
      selectedFilter,
    ],
    "/fetch-cloudwatch-alerts",
    {
      page: 1,
      page_size: ROWS_PER_PAGE,
      company_id,
      site_id: selectedDevices,
      table_name: selectedFilter,
    },
    // @ts-ignore
    {
      ...REFETCH_CONFIGURATION,
      refetchInterval: 1000 * 60 * 3, // Set to 3 minutes
    }
  );

  const formatAlertType = (type: string) => {
    switch (type) {
      case "warning-alerts":
        return t("alert.warning");
      case "critical-alerts-daily":
        return t("alert.critical");
      default:
        return type;
    }
  };

  const isLoading = isNextPageLoading || alertsLoading;

  const tableData =
    data?.results?.map((item: any) => ({
      [t("date_time")]:
        formatCustomDate(item.last_alarm_time) ||
        formatCustomDate(item.sent_at),
      [t("alert.site")]: item.site_id,
      [t("alert.threshold")]: item.threshold,
      [t("alert.type")]: formatAlertType(selectedFilter),
      [t("alert.zone")]: item.region,
      [t("alert.detected_value")]: item.max_value || item.current_value,
      [t("alert.parameter")]: item.metric_type,
    })) ?? [];

  const handlePageChange = (newPage: number) => {
    setIsNextPageLoading(true);
    setPage(newPage);
  };

  // Handle temporary filter change (doesn't trigger refetch)
  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setTempSelectedFilter(e.target.value);
  };

  // Handle temporary device selection change (doesn't trigger refetch)
  const handleDeviceSelectionChange = (devices: string[]) => {
    setTempSelectedDevices(devices);
  };

  // Handle removing a single device from temporary selection
  // const handleRemoveTempDevice = (deviceToRemove: string) => {
  //   setTempSelectedDevices((prev) =>
  //     prev.filter((device) => device !== deviceToRemove)
  //   );
  // };

  // Handle removing a single device from applied selection
  const handleRemoveAppliedDevice = (deviceToRemove: string) => {
    const newSelectedDevices = selectedDevices.filter(
      (device) => device !== deviceToRemove
    );
    setSelectedDevices(newSelectedDevices);
    // Also remove from temporary selection if it exists there
    setTempSelectedDevices((prev) =>
      prev.filter((device) => device !== deviceToRemove)
    );
  };

  // Apply filters and trigger refetch
  const handleApplyFilters = () => {
    setSelectedFilter(tempSelectedFilter);
    setSelectedDevices(tempSelectedDevices);
    setPage(1); // Reset to first page when applying new filters
  };

  // Reset temporary selections
  const handleResetFilters = () => {
    setTempSelectedDevices([...selectedDevices]);
    setTempSelectedFilter(selectedFilter);
  };

  // Check if there are pending changes
  const hasPendingChanges =
    tempSelectedFilter !== selectedFilter ||
    JSON.stringify(tempSelectedDevices.sort()) !==
      JSON.stringify(selectedDevices.sort());

  const {
    data: hierarchyData,
    isLoading: hierarchyLoading,
    error: hierarchyError,
  }: any = useFetchData(
    ["/classifications", "fetchAssetClassifications", company_id],
    "/classifications/",
    {
      company_id,
    },
    // @ts-ignore
    {}
  );

  // Initialize temporary selections with current applied selections
  useEffect(() => {
    if (tempSelectedDevices.length === 0 && selectedDevices.length > 0) {
      setTempSelectedDevices([...selectedDevices]);
    }
  }, [selectedDevices, tempSelectedDevices.length]);

  // Only refetch when page, selectedFilter, or selectedDevices change (not temp values)
  useEffect(() => {
    refetch().finally(() => {
      setIsNextPageLoading(false);
    });
  }, [page, refetch, selectedFilter, selectedDevices]);

  return (
    <CardLayout
      title="Alert History"
      className="relative lg:min-w-[550px] flex-1 !items-start"
    >
      {isLoading ? (
        <div className="flex justify-center items-center h-full w-full">
          <Loader />
        </div>
      ) : error ? (
        <CardError message="Error fetching alerts" />
      ) : !tableData || tableData.length === 0 ? (
        <NoContent message="No alerts found" />
      ) : (
        <div className={`${selectedDevices.length > 0 ? "mt-[4rem]" : "mt-6"}`}>
          {/* Filter section */}
          <div className="!absolute !left-[155px] !top-5 !right-4 z-10  p-3 border-b border-gray-200">
            {/* Filter controls */}
            <div className="flex gap-3 items-start mb-3 ">
              <select
                value={tempSelectedFilter}
                onChange={handleFilterChange}
                className="min-w-[120px] h-[2rem] cursor-pointer flex items-center justify-center py-1 px-2 rounded-full bg-[#E5E5E5] text-primary-blackMain text-[10px]"
              >
                {filter.map((item) => (
                  <option
                    key={item.key}
                    value={item.value}
                    className="text-primary-blackLight cursor-pointer"
                  >
                    {item.key}
                  </option>
                ))}
              </select>

              {hierarchyLoading ? (
                <div className="flex items-center">
                  <Loader2 />
                </div>
              ) : hierarchyError ? (
                <p className="text-red-500 text-xs">
                  {t("error_msg.unable_to_fetch_zone")}
                </p>
              ) : hierarchyData ? (
                <HierarchicalDeviceSelector
                  hierarchy={hierarchyData || []}
                  selectedDevices={tempSelectedDevices}
                  onChange={handleDeviceSelectionChange}
                  selectionField="asset_name"
                  placeholder="Select devices..."
                  className=""
                />
              ) : null}

              <div className="flex gap-2">
                <AppButton
                  type={ButtonType.PRIMARY}
                  text="Apply Filters"
                  handleClick={handleApplyFilters}
                  disabled={!hasPendingChanges}
                  className=" text-xs font-medium"
                />

                {hasPendingChanges && (
                  <button
                    onClick={handleResetFilters}
                    className="px-4 py-1 rounded text-xs font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>

            {/* Selected devices tags - show temporary selections */}
            {/* {tempSelectedDevices.length > 0 && (
              <div className="mb-2">
                <div className="text-xs text-gray-600 mb-1">Pending Selection:</div>
                <SelectedDeviceTags
                  selectedDevices={tempSelectedDevices}
                  onRemoveDevice={handleRemoveTempDevice}
                  maxVisible={3}
                />
              </div>
            )} */}
            {/* Applied filters tags - show currently applied selections */}
            {selectedDevices.length > 0 && (
              <div className="mb-2 min-w-[150px]">
                <div className="text-xs text-gray-600 mb-1">
                  Applied Filters:
                </div>
                <SelectedDeviceTags
                  selectedDevices={selectedDevices}
                  onRemoveDevice={handleRemoveAppliedDevice}
                  maxVisible={5}
                />
              </div>
            )}
          </div>

          {/* Table content with proper top margin to account for fixed filter section */}
          <div className="!self-start">
            <TableTemplate
              data={tableData}
              columns={columns}
              totalCount={data?.total_pages}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={ROWS_PER_PAGE}
            />
          </div>
        </div>
      )}
    </CardLayout>
  );
};

export default AlertHistoryTable;
